import type { FormEvent } from "react";
import { useState } from "react";

import { GradientBackground } from "@/components";
import {
    BenchmarkForm,
    BenchmarkResultPreview,
    BenchmarkTitle,
} from "@/components/benchmark";
import { strings } from "@/constants";
import {
    BenchmarkLayoutVariantEnum,
    BenchmarkPageStatusEnum,
    GradientVariantEnum,
    LanguageEnum,
} from "@/enums";
import { useBenchmarkPolling } from "@/hooks";
import { BenchmarkLayout } from "@/layouts";
import type { BenchmarkPageStatusType, LanguageType } from "@/types";
import { downloadBenchmarkXlsxHandler } from "@/utils";

const mapStatusHandler = (status: ReturnType<typeof useBenchmarkPolling>["status"]): BenchmarkPageStatusType => {
    if (status === "loading") return BenchmarkPageStatusEnum.LOADING;

    if (status === "error") return BenchmarkPageStatusEnum.ERROR;

    if (status === "success") return BenchmarkPageStatusEnum.SUCCESS;

    return BenchmarkPageStatusEnum.IDLE;
};

const buildFileNameHandler = (jobTitle: string): string => {
    const slug = jobTitle.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "result";

    return `${strings.result.xlsxFileNamePrefix}-${slug}`;
};

export const BenchmarkPage = () => {
    const [industry, setIndustry] = useState("");

    const [jobDescription, setJobDescription] = useState("");

    const [jobTitle, setJobTitle] = useState("");

    const [lang, setLang] = useState<LanguageType>(LanguageEnum.EN);

    const [seniority, setSeniority] = useState("");

    const {
        errorMessage,
        onProcess: processBenchmarkHandler,
        onReset: resetPollingHandler,
        progress,
        result,
        status: pollingStatus,
    } = useBenchmarkPolling({ enableCache: true });

    const isFormValid = industry.trim().length > 0
        && jobTitle.trim().length > 0
        && seniority.trim().length > 0
        && jobDescription.trim().length > 0;

    const status = mapStatusHandler(pollingStatus);

    const layoutVariant = status === BenchmarkPageStatusEnum.ERROR ? BenchmarkLayoutVariantEnum.ERROR : BenchmarkLayoutVariantEnum.DEFAULT;

    const submitHandler = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isFormValid) return;

        void processBenchmarkHandler({
            industry: industry.trim(),
            jobDescription: jobDescription.trim(),
            jobTitle: jobTitle.trim(),
            seniority: seniority.trim(),
        });
    };

    const resetHandler = () => {
        resetPollingHandler();

        setIndustry("");

        setJobTitle("");

        setSeniority("");

        setJobDescription("");
    };

    const downloadHandler = () => {
        if (!result) return;

        downloadBenchmarkXlsxHandler(
            {
                industry: result.industry,
                jobTitle: result.jobTitle,
                result: result.resultPayload,
                seniority: result.seniority,
            },
            buildFileNameHandler(result.jobTitle),
        );
    };

    const isSuccess = status === BenchmarkPageStatusEnum.SUCCESS && result;

    return (
        <BenchmarkLayout variant={layoutVariant}>
            {(status === BenchmarkPageStatusEnum.LOADING || status === BenchmarkPageStatusEnum.ERROR) && <GradientBackground variant={status === BenchmarkPageStatusEnum.LOADING ? GradientVariantEnum.LOADING : GradientVariantEnum.ERROR} />}
            <div
                className={`
                    w-full
                    px-4
                    text-center
                    sm:px-8
                    ${isSuccess ? "-my-4 flex h-[calc(100%+2rem)] max-w-6xl self-stretch sm:-my-6 sm:h-[calc(100%+3rem)]" : "max-w-2xl"}
                `}
            >
                {!isSuccess && (
                    <>
                        <BenchmarkTitle
                            errorMessage={errorMessage}
                            progress={progress}
                            status={status}
                        />
                        {status !== BenchmarkPageStatusEnum.LOADING && (
                            <BenchmarkForm
                                industry={industry}
                                isFormValid={isFormValid}
                                jobDescription={jobDescription}
                                jobTitle={jobTitle}
                                lang={lang}
                                seniority={seniority}
                                status={status}
                                onChangeIndustry={setIndustry}
                                onChangeJobDescription={setJobDescription}
                                onChangeJobTitle={setJobTitle}
                                onChangeLang={setLang}
                                onChangeSeniority={setSeniority}
                                onReset={resetHandler}
                                onSubmit={submitHandler}
                            />
                        )}
                    </>
                )}
                {isSuccess && (
                    <BenchmarkResultPreview
                        industry={result.industry}
                        jobTitle={result.jobTitle}
                        result={result.resultPayload}
                        seniority={result.seniority}
                        onDownload={downloadHandler}
                        onReset={resetHandler}
                    />
                )}
            </div>
        </BenchmarkLayout>
    );
};
